/**
 * Цели (core-loop-v2 §4): мечта, цель года и вехи.
 *
 * Мечта — долгая цель. В DREAM_AGE тик предлагает DREAMS_OFFERED мечты под
 * страну и семью (`offerDreams`), игрок выбирает одну (`resolveDream`), либо
 * автопилот берёт первую. Прогресс (`dreamProgress`, 0..1) считается от
 * состояния жизни; при 1 тик ставит `achievedAt`, пишет запись и веху.
 *
 * Цель года — короткая подсказка по состоянию (`yearGoalFor`): погасить долг,
 * найти работу, поднять здоровье… Выполнена (`goalDone`) — запись и +2
 * счастья; не выполнена — остаётся на следующий год. Цель хранится в жизни
 * как данные (без функций), чтобы сохранения оставались JSON.
 *
 * Вехи (`MILESTONES`, `checkMilestones`) — строки документа, не ачивки:
 * первая работа, первые 1000 $, своё жильё, брак, ребёнок, пенсия, десять
 * лет без курения, мечта. Каждая штампуется один раз (`life.milestones`).
 *
 * Ни мечта, ни цель, ни вехи не трогают жребии других подсистем: свои
 * `fork`, никаких изменений вероятностей (тест «dream never changes
 * probabilities»).
 */
import { getCountry, getValue, type Dataset, type WhoRegion } from '@life/data';
import { FLAG_RETIRED } from './decisions/defs';
import { FLAG_TRAVELED } from './events/defs';
import { clampStats } from './focus';
import { HOUSING_OWN_MONEY_GDP } from './look';
import type { Rng } from './rng';
import type { Life, LogEntry } from './types';

export type DreamId = 'snow' | 'home' | 'university' | 'family' | 'abroad' | 'business' | 'eighty' | 'children' | 'travel' | 'peace';

export interface Dream {
  id: DreamId;
  chosenAt: number;
  achievedAt: number | null;
}

export const DREAM_IDS: readonly DreamId[] = ['snow', 'home', 'university', 'family', 'abroad', 'business', 'eighty', 'children', 'travel', 'peace'];

/** Возраст, в котором игра предлагает мечту. */
export const DREAM_AGE = 12;
/** Сколько мечт предлагается на выбор. */
export const DREAMS_OFFERED = 3;
/** Счастье за сбывшуюся мечту. */
export const DREAM_HAPPINESS = 6;
export const DREAM_CHOSEN_KEY = 'dream.chosen';
export const DREAM_ACHIEVED_KEY = 'dream.achieved';

/** Возраст мечты «дожить до восьмидесяти». */
export const DREAM_EIGHTY_AGE = 80;
/** Мечта «покой»: счастье не ниже и возраст не младше. */
const PEACE_HAPPINESS = 80;
const PEACE_MIN_AGE = 60;
/** Мечта «большая семья»: партнёр и столько детей. */
const FAMILY_CHILDREN = 2;
/** Деньги на поездку в долях ВВП на душу: «увидеть снег» и «путешествие». */
const TRAVEL_MONEY_GDP = 0.3;
/** Запись об успешном переезде (decisions/defs.ts, move.abroad). */
const ABROAD_SUCCESS_KEY = 'decision.move.abroad.go.success';
/** Регионы ВОЗ, где снег — редкость: там мечта «увидеть снег» в наборе. */
const WARM_REGIONS: readonly WhoRegion[] = ['AFR', 'SEAR', 'EMR'];
const EDUCATION_RANK: Readonly<Record<Life['education'], number>> = {
  none: 0,
  primary: 1,
  lowerSec: 2,
  upperSec: 3,
  vocational: 4,
  university: 5,
};
const MAX_JOB_LEVEL = 3;

function dreamKey(id: DreamId): string {
  return `dream.${id}`;
}

function gdpOf(ds: Dataset, life: Life): number {
  return Math.max(0, getValue(ds, life.iso3, 'gdpPpp', life.birthYear + life.age).v);
}

function regionOf(ds: Dataset, life: Life): WhoRegion {
  return getCountry(ds, life.iso3).whoRegion;
}

function clamp01(x: number): number {
  if (!(x >= 0)) return 0;
  return x > 1 ? 1 : x;
}

/** Вес мечты для предложения: 0 — не предлагается. */
function dreamWeight(id: DreamId, life: Life, region: WhoRegion): number {
  switch (id) {
    case 'snow':
      return WARM_REGIONS.includes(region) ? 2 : 0;
    case 'home':
      return life.assets.housing === 'own' ? 0 : 2;
    case 'university':
      return life.education === 'university' ? 0 : life.familyDecile >= 5 ? 2 : 1;
    case 'family':
      return life.siblings >= 2 ? 2 : 1;
    case 'abroad':
      return life.familyDecile <= 4 ? 2 : 1;
    case 'business':
      return life.urban ? 2 : 1;
    case 'eighty':
      return 1;
    case 'children':
      return 1;
    case 'travel':
      return life.flags[FLAG_TRAVELED] ? 0 : 1;
    case 'peace':
      return 1;
    default:
      return 0;
  }
}

/** Три мечты без повторов, веса по стране и семье. Все жребии — из `rng`. */
export function offerDreams(life: Life, ds: Dataset, rng: Rng): DreamId[] {
  const region = regionOf(ds, life);
  let pool = DREAM_IDS.filter((id) => dreamWeight(id, life, region) > 0);
  const out: DreamId[] = [];
  while (out.length < DREAMS_OFFERED && pool.length > 0) {
    const picked = rng.weighted(
      pool,
      pool.map((id) => dreamWeight(id, life, region)),
    );
    out.push(picked);
    pool = pool.filter((id) => id !== picked);
  }
  return out;
}

/** Прогресс мечты 0..1 по состоянию; 1 — сбылась. Без мечты — 0. */
export function dreamProgress(life: Life, ds: Dataset): number {
  const dream = life.dream;
  if (dream === null) return 0;
  if (dream.achievedAt !== null) return 1;
  const gdp = gdpOf(ds, life);
  const moneyShare = (share: number): number => (gdp > 0 ? clamp01(life.money / (share * gdp)) : 0);
  switch (dream.id) {
    case 'snow':
    case 'travel':
      return life.flags[FLAG_TRAVELED] ? 1 : Math.min(0.9, moneyShare(TRAVEL_MONEY_GDP));
    case 'home':
      return life.assets.housing === 'own' ? 1 : Math.min(0.9, moneyShare(HOUSING_OWN_MONEY_GDP));
    case 'university':
      return life.education === 'university' ? 1 : (EDUCATION_RANK[life.education] / EDUCATION_RANK.university) * 0.9;
    case 'family':
      return life.partner && life.children >= FAMILY_CHILDREN ? 1 : (life.partner ? 0.4 : 0) + Math.min(FAMILY_CHILDREN - 1, life.children) * 0.25;
    case 'abroad':
      return life.log.some((e) => e.key === ABROAD_SUCCESS_KEY) ? 1 : Math.min(0.9, moneyShare(1));
    case 'business':
      return life.job !== null && life.job.level >= MAX_JOB_LEVEL ? 1 : ((life.job?.level ?? 0) / MAX_JOB_LEVEL) * 0.9;
    case 'eighty':
      return clamp01(life.age / DREAM_EIGHTY_AGE);
    case 'children':
      return life.children >= 1 ? 1 : life.partner ? 0.5 : 0;
    case 'peace':
      return life.stats.happiness >= PEACE_HAPPINESS && life.age >= PEACE_MIN_AGE
        ? 1
        : Math.min(0.9, clamp01(life.stats.happiness / PEACE_HAPPINESS) * clamp01(life.age / PEACE_MIN_AGE));
    default:
      return 0;
  }
}

/**
 * Выбор мечты из предложенных. Мечта вне `pendingDream` отвергается: жизнь
 * возвращается как есть, записей нет.
 */
export function resolveDream(life: Life, id: DreamId): { life: Life; entries: LogEntry[] } {
  if (life.pendingDream === null || !life.pendingDream.includes(id)) return { life, entries: [] };
  const dream: Dream = { id, chosenAt: life.age, achievedAt: null };
  return {
    life: { ...life, dream, pendingDream: null },
    entries: [{ age: life.age, key: DREAM_CHOSEN_KEY, params: { dream: dreamKey(id) } }],
  };
}

/** Мечта сбылась в этом году: отметка, запись и счастье. Иначе — жизнь как есть. */
export function achieveDream(life: Life, ds: Dataset): { life: Life; entries: LogEntry[] } {
  const dream = life.dream;
  if (dream === null || dream.achievedAt !== null || dreamProgress(life, ds) < 1) return { life, entries: [] };
  const next: Life = {
    ...life,
    dream: { ...dream, achievedAt: life.age },
    stats: clampStats({ ...life.stats, happiness: life.stats.happiness + DREAM_HAPPINESS }),
  };
  return { life: next, entries: [{ age: life.age, key: DREAM_ACHIEVED_KEY, params: { dream: dreamKey(dream.id) }, starred: true }] };
}

// ---------------------------------------------------------------------------
// Цель года.

export type GoalId = 'payDebt' | 'findJob' | 'health' | 'happiness' | 'relations' | 'save' | 'knowledge';

export interface Goal {
  id: GoalId;
  /** Ключ i18n `goal.<id>`. */
  key: string;
  params: Record<string, number | string>;
  setAt: number;
}

export const GOAL_DONE_HAPPINESS = 2;
export const GOAL_DONE_KEY = 'goal.done';
/** Пороги целей года. */
const GOAL_HEALTH = 50;
const GOAL_HAPPINESS = 40;
const GOAL_RELATIONS = 40;
/** Копилка: цель — текущие деньги плюс доля ВВП на душу, округлённая до сотен. */
const GOAL_SAVE_GDP = 0.1;
const GOAL_KNOWLEDGE_STEP = 5;
/** С какого возраста годится цель «найти работу». */
const GOAL_JOB_MIN_AGE = 16;

function goalKey(id: GoalId): string {
  return `goal.${id}`;
}

function goal(id: GoalId, life: Life, params: Record<string, number | string>): Goal {
  return { id, key: goalKey(id), params, setAt: life.age };
}

function target(g: Goal, name: string): number {
  const raw = g.params[name];
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : Number.NaN;
}

/** Выполнена ли цель при этом состоянии жизни. */
export function goalDone(g: Goal, life: Life): boolean {
  switch (g.id) {
    case 'payDebt':
      return life.debt <= 0;
    case 'findJob':
      return life.job !== null;
    case 'health':
      return life.stats.health >= target(g, 'target');
    case 'happiness':
      return life.stats.happiness >= target(g, 'target');
    case 'relations':
      return life.stats.relations >= target(g, 'target');
    case 'save':
      return life.money >= target(g, 'amount');
    case 'knowledge':
      return life.stats.knowledge >= target(g, 'target');
    default:
      return false;
  }
}

/**
 * Одна цель по состоянию, до DREAM_AGE — нет. Приоритет: долг → работа →
 * здоровье → счастье → отношения; иначе жребий между копилкой и знаниями.
 */
export function yearGoalFor(life: Life, ds: Dataset, rng: Rng): Goal | null {
  if (life.age < DREAM_AGE) return null;
  if (life.debt > 0) return goal('payDebt', life, { amount: Math.round(life.debt) });
  if (life.age >= GOAL_JOB_MIN_AGE && !life.inSchool && life.job === null && !life.flags[FLAG_RETIRED]) return goal('findJob', life, {});
  if (life.stats.health < GOAL_HEALTH) return goal('health', life, { target: GOAL_HEALTH });
  if (life.stats.happiness < GOAL_HAPPINESS) return goal('happiness', life, { target: GOAL_HAPPINESS });
  if (life.stats.relations < GOAL_RELATIONS) return goal('relations', life, { target: GOAL_RELATIONS });
  const gdp = gdpOf(ds, life);
  const canSave = gdp > 0;
  const canLearn = life.stats.knowledge + GOAL_KNOWLEDGE_STEP <= 100;
  const save = (): Goal => {
    const amount = Math.max(100, Math.ceil((life.money + GOAL_SAVE_GDP * gdp) / 100) * 100);
    return goal('save', life, { amount });
  };
  const learn = (): Goal => goal('knowledge', life, { target: Math.round(life.stats.knowledge) + GOAL_KNOWLEDGE_STEP });
  if (canSave && canLearn) return rng.chance(0.5) ? save() : learn();
  if (canSave) return save();
  if (canLearn) return learn();
  return null;
}

/** Выполненная цель года: запись и счастье; иначе жизнь как есть. */
export function settleGoal(life: Life): { life: Life; entries: LogEntry[]; done: boolean } {
  const g = life.yearGoal;
  if (g === null || !goalDone(g, life)) return { life, entries: [], done: false };
  const next: Life = { ...life, stats: clampStats({ ...life.stats, happiness: life.stats.happiness + GOAL_DONE_HAPPINESS }) };
  return { life: next, entries: [{ age: life.age, key: GOAL_DONE_KEY, params: { goal: g.key, ...g.params } }], done: true };
}

// ---------------------------------------------------------------------------
// Вехи.

export type MilestoneId = 'firstJob' | 'first1000' | 'ownHome' | 'marriage' | 'firstChild' | 'retire' | 'tenYearsNoSmoking' | 'dream';

export interface MilestoneDef {
  id: MilestoneId;
  check: (life: Life, prev: Life) => boolean;
}

export const MILESTONE_MONEY = 1000;
export const NO_SMOKING_YEARS = 10;
/** Запись события «бросить курить» (events/defs.ts): отсчёт десяти лет. */
export const SMOKING_QUIT_KEY = 'event.habit.smokingQuit';

function quitSmokingAge(life: Life): number | null {
  let last: number | null = null;
  for (const e of life.log) if (e.key === SMOKING_QUIT_KEY) last = e.age;
  return last;
}

export const MILESTONES: readonly MilestoneDef[] = [
  { id: 'firstJob', check: (life) => life.job !== null },
  { id: 'first1000', check: (life) => life.money >= MILESTONE_MONEY },
  { id: 'ownHome', check: (life) => life.assets.housing === 'own' },
  { id: 'marriage', check: (life) => life.partner },
  { id: 'firstChild', check: (life) => life.children >= 1 },
  { id: 'retire', check: (life) => Boolean(life.flags[FLAG_RETIRED]) },
  {
    id: 'tenYearsNoSmoking',
    check: (life) => {
      if (life.habits.smoking) return false;
      const quit = quitSmokingAge(life);
      return quit !== null && life.age - quit >= NO_SMOKING_YEARS;
    },
  },
  { id: 'dream', check: (life) => life.dream !== null && life.dream.achievedAt !== null },
];

export function milestoneKey(id: string): string {
  return `milestone.${id}`;
}

/** Вехи, наступившие в `next` и ещё не отмеченные в `prev.milestones`; каждая — один раз. */
export function checkMilestones(prev: Life, next: Life): string[] {
  const seen = new Set<string>([...prev.milestones, ...next.milestones]);
  const fired: string[] = [];
  for (const m of MILESTONES) if (!seen.has(m.id) && m.check(next, prev)) fired.push(m.id);
  return fired;
}

/** Штамп вех в жизнь: список и помеченные записи. */
export function stampMilestones(prev: Life, next: Life): { life: Life; entries: LogEntry[] } {
  const fired = checkMilestones(prev, next);
  if (fired.length === 0) return { life: next, entries: [] };
  return {
    life: { ...next, milestones: [...next.milestones, ...fired] },
    entries: fired.map((id) => ({ age: next.age, key: milestoneKey(id), starred: true })),
  };
}
